import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { titleize, humanize } from 'underscore.string';
import {
  BadgeAlt as Badge,
  Checkbox,
  DataTableColumnHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cargomatic/ui';

const ROLE_BADGE_CLASS = 'bg-blue-50 text-blue-700 border-blue-200';
const VISIBLE_ROLE_COUNT = 3;

function StatusBadge({ active }) {
  if (active) {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-700 border-green-200"
      >
        Active
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
      Deactivated
    </Badge>
  );
}

function RolesCell({ roles }) {
  const list = Array.isArray(roles) ? roles : [];
  if (!list.length) return null;
  const visible = list.slice(0, VISIBLE_ROLE_COUNT);
  const hidden = list.slice(VISIBLE_ROLE_COUNT);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((role) => (
        <Badge key={role} variant="outline" className={ROLE_BADGE_CLASS}>
          {titleize(humanize(role))}
        </Badge>
      ))}
      {hidden.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              +{hidden.length}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto max-w-xs p-2">
            <div className="flex flex-wrap gap-1">
              {hidden.map((role) => (
                <Badge
                  key={role}
                  variant="outline"
                  className={ROLE_BADGE_CLASS}
                >
                  {titleize(humanize(role))}
                </Badge>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

export function getAdminUsersColumns() {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      size: 32,
    },
    {
      accessorKey: 'first_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="First Name" />
      ),
      cell: ({ row }) => row.original.first_name,
      size: 140,
    },
    {
      accessorKey: 'last_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Name" />
      ),
      cell: ({ row }) => row.original.last_name,
      size: 140,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Email" />
      ),
      cell: ({ row }) => (
        <Link
          to={`/users/${row.original._id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {row.original.email}
        </Link>
      ),
      size: 240,
    },
    {
      id: 'roles',
      header: 'Roles',
      cell: ({ row }) => <RolesCell roles={row.original.roles} />,
      enableSorting: false,
    },
    {
      accessorKey: 'last_login_session',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Seen" />
      ),
      cell: ({ row }) => {
        const ts = row.original.last_login_session;
        return ts ? moment(ts).format('MM/DD/YYYY') : '—';
      },
      size: 120,
    },
    {
      accessorKey: 'active',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => <StatusBadge active={!!row.original.active} />,
      size: 120,
    },
  ];
}
